import { type CloudEventTrace, CloudEventTraceSchema } from '@pkg/shared/trace'
import { generateUuid } from '@pkg/shared/utils/id.utils'
import Hook from 'before-after-hook'
import { createNanoEvents } from 'nanoevents'
import { z } from 'zod'

export interface AsyncMeta<T = unknown> {
  resolve: (value: T) => void
  reject: (reason?: unknown) => void
}

// Internal event shape on the per-registry nanoevents bus
interface BusEvent<TData> {
  data: TData
  meta?: AsyncMeta
}

// Event definition: event type name + explicit version + payload schema
export interface EventDef<TType extends string, TSchema extends z.core.SomeType, TData> {
  type: TType
  schema: TSchema
  build: (d: TData, meta?: PublishMeta) => CloudEvent<TData>
  is: (payload: unknown) => payload is z.infer<TSchema>
  // Hook API — attach middleware directly on the event def
  before: (fn: (data: TData) => void | Promise<void>) => void
  after: (fn: (result: unknown, data: TData) => void | Promise<void>) => void
  error: (fn: (err: Error, data: TData) => void | Promise<void>) => void
  wrap: (fn: (original: (data: TData) => Promise<unknown>, data: TData) => Promise<unknown>) => void
  // Bus API
  on: (handler: (data: TData) => Promise<unknown>) => () => void
  emit: (data: TData) => void
  emitAsync: (data: TData) => Promise<unknown>
}

export type AnyEventDef = EventDef<string, z.ZodTypeAny, unknown>

// Publisher interfaces (transport-agnostic)
export type RouteMeta = { bus?: string; stage?: string; region?: string; channel?: string; topic?: string }
export interface EventPublisher {
  publish<T>(event: CloudEvent<T>, opts?: { route?: RouteMeta }): Promise<void>
  publishBatch: (events: CloudEvent<unknown>[], opts?: { route?: RouteMeta }) => Promise<void>
}

export const CloudEventMeta = z.object({
  flow: z.enum(['async', 'sync']).default('async'),
})

// Follows official https://github.com/cloudevents/spec/blob/v1.0/spec.md#event-format
export const CloudEvent = z.object({
  specversion: z.literal('1.0').default('1.0'),
  type: z.string().min(1),
  source: z.string(), // emitting system identifier
  id: z.string().default(() => generateUuid()),
  time: z.iso.datetime().default(() => new Date().toISOString()),
  subject: z.string().nullish(),
  datacontenttype: z.string().default('application/json'),
  data: z.any(),
  trace: CloudEventTraceSchema.optional(),
  meta: CloudEventMeta.default({
    flow: 'async',
  }),
  stages: z.array(z.string()).optional(), // optional routing directive
})

export type CloudEvent<TData> = z.infer<typeof CloudEvent> & { data?: TData }
export type CloudEventMeta = z.infer<typeof CloudEventMeta>

export type AnyCloudEvent = CloudEvent<unknown>

export interface PublishMeta {
  id?: string
  time?: string
  trace?: CloudEventTrace
  meta?: CloudEventMeta
  stages?: string[]
}

export function defineEventRegistry<TTypePrefix extends string, TSource extends string>(registry: {
  typePrefix: TTypePrefix
  source: TSource
}) {
  const eventMap = new Set<string>()
  const defs: AnyEventDef[] = []
  const byFullType = new Map<string, AnyEventDef>()

  // Shared untyped bus for this registry. Type safety is provided by each event def's typed methods.
  const bus = createNanoEvents<Record<string, (event: BusEvent<unknown>) => void>>()

  function defineEvent<const TType extends string, const TDataSchema extends z.ZodTypeAny>(args: {
    type: TType
    data: TDataSchema
  }) {
    const { type, data } = args
    if (eventMap.has(type)) {
      throw new Error(`Event type ${type} already defined`)
    }

    const fullType = `${registry.typePrefix}.${type}` as const

    const schema = CloudEvent.extend({
      // Use a literal so `z.infer<typeof schema>['type']` stays a strict string literal.
      // `z.templateLiteral(...)` would widen inference to plain `string`.
      type: z.literal(fullType),
      source: z.literal(registry.source),
      data,
    })

    const build = (d: z.infer<typeof data>, meta?: PublishMeta) => {
      return schema.parse({
        id: meta?.id,
        time: meta?.time,
        stages: meta?.stages,
        // Do not auto-create trace ids — only include when provided by publisher from OTel context
        trace: meta?.trace,
        type: fullType,
        source: registry.source,
        meta: meta?.meta,
        data: d,
      })
    }

    const is = (d: unknown): d is z.infer<typeof schema> => {
      return typeof d === 'object' && d !== null && 'type' in d && typeof d?.type === 'string' && d.type === fullType
    }

    type TData = z.infer<TDataSchema>

    // Per-event hook — middleware is attached directly (e.g. PatientClinicAdd.before(fn))
    const hook = new Hook.Singular<TData, unknown, Error>()

    const on = (handler: (data: TData) => Promise<unknown>): (() => void) => {
      return bus.on(fullType, event => {
        const { meta, data: eventData } = event as BusEvent<TData>
        void hook(() => handler(eventData), eventData)
          .then(result => meta?.resolve?.(result))
          .catch((err: unknown) => meta?.reject?.(err))
      })
    }

    const emit = (eventData: TData): void => {
      bus.emit(fullType, { data: eventData })
    }

    const emitAsync = (eventData: TData): Promise<unknown> => {
      let resolve!: (v: unknown) => void
      let reject!: (e: unknown) => void
      const promise = new Promise<unknown>((res, rej) => {
        resolve = res
        reject = rej
      })
      bus.emit(fullType, { data: eventData, meta: { resolve, reject } })
      return promise
    }

    eventMap.add(type)
    const def = {
      type: fullType,
      schema,
      build,
      is,
      before: hook.before.bind(hook),
      after: hook.after.bind(hook),
      error: hook.error.bind(hook),
      wrap: hook.wrap.bind(hook),
      on,
      emit,
      emitAsync,
    } as EventDef<`${TTypePrefix}.${TType}`, typeof schema, z.infer<TDataSchema>>

    // register in collections for whole-registry binding
    defs.push(def as AnyEventDef)
    byFullType.set(fullType, def as AnyEventDef)

    return def
  }

  return {
    typePrefix: registry.typePrefix,
    source: registry.source,
    defineEvent,
    all: () => defs.slice() as readonly AnyEventDef[],
    byType: () => Object.fromEntries(byFullType.entries()) as Record<string, AnyEventDef>,
    types: () => Array.from(byFullType.keys()),
  }
}

export function isCloudEvent(v: unknown): v is CloudEvent<unknown> {
  return (
    !!v &&
    typeof v === 'object' &&
    'type' in v &&
    'source' in v &&
    typeof v.type === 'string' &&
    typeof v.source === 'string'
  )
}

export type PayloadOf<D extends { payload: z.ZodTypeAny }> = z.infer<D['payload']>
